// ── Collateral tab — collateral headers/lines on a loan ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, fmt, fmtDate } from "../../../shared/format";
import { Btn, Card, Icon, PanelTitle, Pagination, STh, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { usePagination, useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { calcCollateralLine, calcCollateralHeader } from "../../../shared/collateral";

// title: the loan it belongs to ("LN-101 — Revolver"), shown INSIDE the cards
// rather than as a heading above them, when the account page lists several loans.
export const CollateralTab = ({ loanid, canEdit, canDelete, excessAvailability, excessComponents, participationFactor = 1, title }) => {
    const { api, setConfirm, setModal, view, setError } = useApp();
    const [headers, setHeaders]         = useState([]);
    const [recentLines, setRecentLines] = useState([]);
    const [loading, setLoading]         = useState(true);

    const load = () => {
      setLoading(true);
      api.get('getCollateralHeaders?loanid=' + loanid)
        .then(async d => {
          setHeaders(d);
          // Load lines for the most recent header
          if (d.length > 0) {
            const most = d.reduce((a,b) => new Date(a.asofdate) > new Date(b.asofdate) ? a : b);
            try {
              const detail = await api.get('getCollateralHeaders?id=' + most.id);
              setRecentLines(detail.lines || []);
            } catch(e) { setRecentLines([]); }
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    };
    useEffect(() => { load(); }, [loanid]);

    const handleNew = async () => {
      try {
        const result = await api.post('saveCollateralHeader', {
          loanid,
          asofdate: new Date().toISOString().slice(0, 10),
          carryforward: true
        });
        setModal({ type: 'collateral-entry', headerId: result.id, loanid, onDone: load });
      } catch(e) { setError(e.message); }
    };

    const { sorted, sortKey, sortDir, toggle } = useSortable(headers, 'asofdate', 'desc');
    const { paginated: displayed, page, setPage, totalPages, total } = usePagination(sorted, 50);
    // Most-recent detail lines, with their calculated values as sort keys.
    const detailRows = recentLines.map(l => {
      const c = calcCollateralLine(l);
      return { ...l, _eligible: c.eligible, _predil: c.valuepredilution, _collval: c.collateralvalue };
    });
    const det = useSortable(detailRows, 'collateraltype', 'asc');

    if (loading) return <Spinner />;

    // ── Most recent header + calculated totals ────────────────────────────
    const mostRecent = headers.length > 0
      ? headers.reduce((a,b) => new Date(a.asofdate) > new Date(b.asofdate) ? a : b)
      : null;
    const headerCalc = mostRecent ? calcCollateralHeader(mostRecent, recentLines) : null;

    // AR lines summary
    const arLines = recentLines.filter(l => l.collateraltype === 'Accounts Receivable');
    const totalARGross     = arLines.reduce((s,l) => s + parseFloat(l.grossamount      ||0), 0);
    const totalARInelig    = arLines.reduce((s,l) => s + parseFloat(l.ineligibleamount ||0), 0);
    const minARAdvanceRate = arLines.length ? Math.min(...arLines.map(l => parseFloat(l.advancerate||0))) : 0;
    const totalARReserves  = arLines.reduce((s,l) => s + parseFloat(l.otherreserves    ||0), 0);
    const totalARCollValue = arLines.reduce((s,l) => s + calcCollateralLine(l).collateralvalue, 0);

    const SH = ({ title, color }) => (
      <div style={{ fontSize: 11, fontWeight: 600, color: color || B.cyanDark, textTransform: 'uppercase',
        letterSpacing: '0.06em', paddingBottom: 6, marginBottom: 12,
        borderBottom: '1px solid ' + (color ? B.amberLight : B.cyanLight) }}>
        {title}
      </div>
    );
    const KV = ({ label, value, bold }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5, gap: 8 }}>
        <span style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}>{label}</span>
        <span style={{ fontWeight: bold ? 700 : 500, textAlign: 'right' }}>{value}</span>
      </div>
    );

    return <>
      {/* ── Summary Cards ── */}
      {mostRecent && headerCalc && (
        <div style={{ marginBottom: 20 }}>

          {/* Row 1 — top level summary */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12,
            padding: '10px 14px', background: B.cyanLight, borderRadius: 8,
            borderLeft: '4px solid ' + B.cyan, flexWrap: 'wrap' }}>
            {title && <div style={{ fontSize: 13, fontWeight: 700, color: B.cyanDark, marginRight: 8 }}>{title}</div>}
            <div>
              <span style={{ fontSize: 11, color: B.cyanDark, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Collateral As of Date</span>
              <span style={{ fontSize: 13, fontWeight: 700, marginLeft: 10 }}>{fmtDate(mostRecent.asofdate)}</span>
            </div>
            <div style={{ marginLeft: 24 }}>
              <span style={{ fontSize: 11, color: B.cyanDark, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>⭐ Total Collateral Value</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: B.cyanDark, marginLeft: 10 }}>{fmt(headerCalc.totalCollateralValue * participationFactor)}</span>
            </div>
            <div style={{ marginLeft: 24 }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gross Availability Reserves</span>
              <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 10 }}>{fmt(mostRecent.grossavailabilityreserves || 0)}</span>
            </div>
            {excessAvailability != null && (
              <div style={{ marginLeft: 24, paddingLeft: 24, borderLeft: '1px solid ' + B.cyan }}>
                <span style={{ fontSize: 11, color: B.cyanDark, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>⭐ Excess Availability</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: parseFloat(excessAvailability) >= 0 ? '#0F6E56' : '#993C1D', marginLeft: 10 }}>
                  {fmt(excessAvailability)}
                </span>
              </div>
            )}
            {/* Component breakdown for troubleshooting */}
            {excessComponents && excessComponents.arvalue > 0 && (
              <div style={{ width: '100%', marginTop: 8, paddingTop: 8, borderTop: '0.5px solid ' + B.cyan,
                display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                <span>AR Value: <strong>{fmt(excessComponents.arvalue)}</strong></span>
                <span>AR Advance Sum: <strong>{fmt(excessComponents.aradvancesum)}</strong></span>
                <span>AR Reserves: <strong>{fmt(excessComponents.arreserves)}</strong></span>
                <span>Total AR Coll Value: <strong>{fmt(excessComponents.totalarcollateralvalue)}</strong></span>
                <span>Rcvd Since BBC: <strong>{fmt(excessComponents.receivedprincsince)}</strong></span>
                <span>Over Advance: <strong>{fmt(excessComponents.overadvanceallowance)}</strong></span>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>

            {/* AR Summary */}
            {arLines.length > 0 && (
              <Card style={{ padding: 14 }}>
                <SH title="Most Recent AR Collateral" />
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                  Total AR used for Collateral Value Calculated
                </div>
                <KV label="Total AR Gross Amount"    value={fmt(totalARGross)} />
                <KV label="Total AR Ineligible Amount" value={fmt(totalARInelig)} />
                <KV label="Min AR Advance Rate %"    value={minARAdvanceRate.toFixed(1) + '%'} />
                <KV label="Total AR Other Reserves"  value={fmt(totalARReserves)} />
                <KV label="⭐ Total AR Collateral Value" value={fmt(totalARCollValue)} bold />
              </Card>
            )}

            {/* Inventory Summary */}
            {recentLines.some(l => l.collateraltype === 'Inventory') && (
              <Card style={{ padding: 14 }}>
                <SH title="Most Recent Inventory Collateral" />
                <KV label="Total Available Inventory"  value={fmt(headerCalc.totalAvailableInventory)} />
                <KV label="Aggregate Inventory Sublimit" value={fmt(mostRecent.aggregateinventorysublimit || 0)} />
                <KV label="Aggregate Inventory Reserve"  value={fmt(mostRecent.aggregateinventoryreserve  || 0)} />
                <KV label="⭐ Net Available Inventory"    value={fmt(headerCalc.netAvailableInventory)} bold />
              </Card>
            )}

            {/* AP Aging Summary */}
            <Card style={{ padding: 14 }}>
              <SH title="Most Recent AP Aging" color={B.amberDark} />
              <KV label="AP 0–30"   value={fmt(mostRecent.apaging0to30  || 0)} />
              <KV label="AP 31–60"  value={fmt(mostRecent.apaging31to60 || 0)} />
              <KV label="AP 61–90"  value={fmt(mostRecent.apaging61to90 || 0)} />
              <KV label="AP 90+"    value={fmt(mostRecent.apaging90plus  || 0)} />
              <KV label="⭐ Total AP Aging" value={fmt(headerCalc.totalAPAging)} bold />
            </Card>

          </div>

          {/* Collateral Detail Lines */}
          <Card style={{ padding: 14 }}>
            <SH title="Most Recent Collateral Detail" />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <STh label="Type" sortKey="collateraltype" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} />
                    <STh label="Gross Amount" sortKey="grossamount" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Ineligible" sortKey="ineligibleamount" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Eligible" sortKey="_eligible" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Advance Rate" sortKey="advancerate" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Pre-Dilution" sortKey="_predil" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Other Reserves" sortKey="otherreserves" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Sublimit" sortKey="sublimit" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                    <STh label="Collateral Value" sortKey="_collval" currentKey={det.sortKey} currentDir={det.sortDir} onSort={det.toggle} numeric />
                  </tr>
                </thead>
                <tbody>
                  {det.sorted.map((line, idx) => {
                    const calc = calcCollateralLine(line);
                    return (
                      <tr key={line.id} style={{ background: idx % 2 === 1 ? 'var(--color-background-secondary)' : '' }}>
                        <Td style={{ fontWeight: 500 }}>{line.collateraltype}</Td>
                        <TdNum>{fmt(line.grossamount      || 0)}</TdNum>
                        <TdNum>{fmt(line.ineligibleamount || 0)}</TdNum>
                        <TdNum>{fmt(calc.eligible)}</TdNum>
                        <TdNum>{parseFloat(line.advancerate || 0).toFixed(2)}%</TdNum>
                        <TdNum>{fmt(calc.valuepredilution)}</TdNum>
                        <TdNum>{fmt(line.otherreserves || 0)}</TdNum>
                        <TdNum>{fmt(line.sublimit      || 0)}</TdNum>
                        <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(calc.collateralvalue)}</TdNum>
                      </tr>
                    );
                  })}
                  {/* Total row */}
                  <tr style={{ background: B.cyanLight, borderTop: '2px solid ' + B.cyan }}>
                    <td style={{ padding: '7px 10px', fontSize: 12, fontWeight: 700, color: B.cyanDark }}>TOTAL</td>
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(recentLines.reduce((s,l)=>s+parseFloat(l.grossamount||0),0))}</TdNum>
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(recentLines.reduce((s,l)=>s+parseFloat(l.ineligibleamount||0),0))}</TdNum>
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(recentLines.reduce((s,l)=>s+calcCollateralLine(l).eligible,0))}</TdNum>
                    <td />
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(recentLines.reduce((s,l)=>s+calcCollateralLine(l).valuepredilution,0))}</TdNum>
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(recentLines.reduce((s,l)=>s+parseFloat(l.otherreserves||0),0))}</TdNum>
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(recentLines.reduce((s,l)=>s+parseFloat(l.sublimit||0),0))}</TdNum>
                    <TdNum style={{ fontWeight: 700, color: B.cyanDark }}>{fmt(headerCalc.totalCollateralValue)}</TdNum>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ── History List ── title inside its card, like every other card */}
      <Card style={{ overflowX: 'auto' }}>
        <PanelTitle title={title ? `Entry History — ${title}` : 'Entry History'} count={headers.length} actions={<>
          <Btn onClick={() => exportCSV(sorted, ['asofdate','linecount','grossavailabilityreserves','overadvanceallowance','aggregateinventorysublimit','aggregateinventoryreserve','apaging0to30','apaging31to60','apaging61to90','apaging90plus'], 'collateral.csv')}>
            Export CSV
          </Btn>
          {canEdit && <Btn variant="primary" onClick={handleNew}>+ New Entry</Btn>}
        </>} />
      {headers.length === 0
        ? <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', padding: '4px 0' }}>No collateral entries recorded.</div>
        : <>
            <table style={{ width: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
              <thead><tr>
                <STh label="As-of Date"        sortKey="asofdate"                    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Lines"             sortKey="linecount"                   currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Gross Amount"      sortKey="sumgross"                    currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Ineligible"        sortKey="suminelig"                   currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Eligible"          sortKey="sumeligible"                 currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Collateral Value"  sortKey="sumcollateralvalue"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Gross Avail Res"   sortKey="grossavailabilityreserves"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Over Advance"      sortKey="overadvanceallowance"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Agg Inv Sublimit"  sortKey="aggregateinventorysublimit"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Agg Inv Reserve"   sortKey="aggregateinventoryreserve"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Total AP Aging"    sortKey="apaging0to30"                currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <Th></Th>
              </tr></thead>
              <tbody>
                {displayed.map((h, idx) => {
                  const totalAP = (parseFloat(h.apaging0to30||0)) + (parseFloat(h.apaging31to60||0)) +
                                  (parseFloat(h.apaging61to90||0)) + (parseFloat(h.apaging90plus||0));
                  return (
                    <tr key={h.id}
                      style={{ cursor: 'pointer', background: idx % 2 === 1 ? 'var(--color-background-secondary)' : '' }}
                      onClick={() => setModal({ type: 'collateral-entry', headerId: h.id, loanid, onDone: load })}
                      onMouseEnter={e => e.currentTarget.style.background = B.cyanLight}
                      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 1 ? 'var(--color-background-secondary)' : ''}>
                      <Td style={{ fontWeight: 500 }}>{fmtDate(h.asofdate)}</Td>
                      <TdNum>{h.linecount}</TdNum>
                      <TdNum>{fmt(h.sumgross || 0)}</TdNum>
                      <TdNum>{fmt(h.suminelig || 0)}</TdNum>
                      <TdNum>{fmt(h.sumeligible || 0)}</TdNum>
                      <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(h.sumcollateralvalue || 0)}</TdNum>
                      <TdNum>{fmt(h.grossavailabilityreserves || 0)}</TdNum>
                      <TdNum>{fmt(h.overadvanceallowance || 0)}</TdNum>
                      <TdNum>{fmt(h.aggregateinventorysublimit || 0)}</TdNum>
                      <TdNum>{fmt(h.aggregateinventoryreserve || 0)}</TdNum>
                      <TdNum>{fmt(totalAP)}</TdNum>
                      <Td><div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                        <Btn onClick={() => setModal({ type: 'collateral-entry', headerId: h.id, loanid, onDone: load })}>
                          <Icon type={canEdit ? 'edit' : 'view'} />
                        </Btn>
                        {canDelete && (
                          <Btn variant="danger" onClick={() => setConfirm({
                            msg: `Delete collateral entry for ${fmtDate(h.asofdate)}? This will also delete all lines.`,
                            onConfirm: async () => {
                              setConfirm(null);
                              try { await api.post('deleteCollateralHeader', { id: h.id }); } catch (e) { setError(e.message); }
                              load();
                            }
                          })}>
                            <Icon type="delete" />
                          </Btn>
                        )}
                      </div></Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={50} onPage={setPage} />
          </>
      }
      </Card>
    </>;
  };
